// creativeMedia — resolve a graph node's media into something the Brain page
// and the brief generator can actually work with (a fetchable video URL, a
// thumbnail, and — where possible — the raw bytes for frame sampling).
//
// The Web graph nodes come from Meta (metaToWeb) or seed data. Meta video
// nodes only carry a numeric video_id, so the CDN source is resolved on demand
// via /api/meta/video. Thumbnails are always available as a fallback.

import { apiFetch } from "./apiClient";

const isFetchableUrl = (url) => {
  const v = String(url || "").trim();
  return v.startsWith("http://") || v.startsWith("https://");
};

export const nodeIsVideo = (node) => {
  if (!node) return false;
  if (node.video_id) return true;
  return String(node.format || "").startsWith("video");
};

/**
 * resolveCreativeMedia(node) → {
 *   kind: 'video' | 'image',
 *   videoUrl: string,   // playable https source, when resolvable
 *   imageUrl: string,   // thumbnail / static image
 *   videoId: string,
 *   label: string,
 * }
 *
 * Best-effort: if the Meta video source can't be resolved (integration off,
 * expired link) we still return the thumbnail so downstream analysis degrades
 * to image mode instead of failing.
 */
export const resolveCreativeMedia = async (node) => {
  const kind = nodeIsVideo(node) ? "video" : "image";
  const imageUrl = node?.thumbnail || node?._meta?.thumbnail_url || "";
  let videoUrl = "";
  const videoId = node?.video_id || node?._meta?.video_id || "";

  if (kind === "video" && videoId) {
    try {
      const data = await apiFetch(
        `/api/meta/video?video_id=${encodeURIComponent(videoId)}`,
      );
      if (isFetchableUrl(data?.source)) videoUrl = data.source;
    } catch (error) {
      // Fall back to thumbnail-only analysis.
    }
  }

  return {
    kind: videoUrl ? "video" : "image",
    requestedKind: kind,
    videoUrl,
    imageUrl,
    videoId,
    label: node?.label || "creative",
  };
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(blob);
  });

/**
 * Fetch a remote media URL as a File so it can be forwarded to the Tribe model
 * as raw bytes (the model server can't reach short-lived CDN links reliably).
 * Returns null on CORS/network failure — callers fall back to URL mode.
 */
export const fetchMediaAsFile = async (url, name = "creative") => {
  if (!isFetchableUrl(url)) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const ext = (blob.type.split("/")[1] || "bin").split(";")[0];
    return new File([blob], `${name}.${ext}`, { type: blob.type });
  } catch (error) {
    return null;
  }
};

/**
 * extractKeyFrames — sample N evenly spaced frames from a video for visual
 * analysis + the KB. `source` may be a File/Blob (uploaded creative) or a
 * fetchable video URL (resolved Meta video). For images — or when video capture
 * fails, e.g. a tainted cross-origin canvas — it returns the still as one frame.
 *
 * @returns Array<{ timeSec: number, dataUrl: string, label: string }>
 */
export const extractKeyFrames = async ({
  aiService,
  source,
  kind = "video",
  imageUrl = "",
  count = 5,
  signal,
} = {}) => {
  const still = imageUrl ? [{ timeSec: 0, dataUrl: imageUrl, label: "thumbnail" }] : [];

  if (kind !== "video" || !source) {
    // Image source as a Blob → render it as the single frame.
    if (source instanceof Blob) {
      try {
        return [{ timeSec: 0, dataUrl: await blobToDataUrl(source), label: "frame" }];
      } catch (error) {
        return still;
      }
    }
    return still;
  }

  const objectUrl = source instanceof Blob ? URL.createObjectURL(source) : null;
  try {
    const probeUrl = objectUrl || source;
    const duration = await probeDuration(probeUrl, signal).catch(() => 0);
    const total = Number.isFinite(duration) && duration > 1 ? duration : 0;

    // Even spread across the clip; if duration is unknown, sample the early
    // seconds where the hook + first drop-off live.
    const stamps = total
      ? Array.from({ length: count }, (_, i) =>
          Number(((total * (i + 0.5)) / count).toFixed(2)),
        )
      : [0, 1, 2, 3, 4].slice(0, count);

    const frames = [];
    for (let i = 0; i < stamps.length; i += 1) {
      if (signal?.aborted) break;
      try {
        const { blob } = await aiService.captureVideoFrame(source, stamps[i], signal, {
          maxDim: 720,
          quality: 0.7,
        });
        const dataUrl = await blobToDataUrl(blob);
        frames.push({
          timeSec: stamps[i],
          dataUrl,
          label: total ? `${Math.round((100 * stamps[i]) / total)}%` : `${stamps[i]}s`,
        });
      } catch (error) {
        // A cross-origin taint kills every capture — stop trying.
        if (String(error?.message || "").toLowerCase().includes("taint")) break;
      }
    }
    return frames.length ? frames : still;
  } catch (error) {
    return still;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
};

// Minimal duration probe that doesn't depend on AIService internals.
const probeDuration = (url, signal) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.crossOrigin = "anonymous";
    const cleanup = () => {
      video.removeAttribute("src");
      video.load();
    };
    const onAbort = () => {
      cleanup();
      reject(new Error("aborted"));
    };
    if (signal) {
      if (signal.aborted) return onAbort();
      signal.addEventListener("abort", onAbort, { once: true });
    }
    video.onloadedmetadata = () => {
      const d = Number(video.duration || 0);
      cleanup();
      resolve(d);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("metadata load failed"));
    };
    video.src = url;
  });
